import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DatastoreCollection } from '@freelancer/datastore';
import {
  Certification,
  CertificationsCollection,
} from '@freelancer/datastore/collections';
import { TimeUtils } from '@freelancer/time-utils';
import { BannerAlertType } from '@freelancer/ui/banner-alert';
import { ButtonColor } from '@freelancer/ui/button';
import { Margin } from '@freelancer/ui/margin';
import { SelectAcceptedType } from '@freelancer/ui/select';
import { FontWeight, TextSize } from '@freelancer/ui/text';
import { maxLength, required } from '@freelancer/ui/validators';
import { isFormControl } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { map } from 'rxjs/operators';

enum CertificationFormInput {
  NAME = 'name',
  ORGANIZATION = 'organization',
  SUMMARY = 'summary',
  YEAR = 'year',
}

@Component({
  selector: 'app-user-profile-certifications-edit',
  template: `
    <fl-grid>
      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text
          i18n="Professional Certificate or Award label"
          [weight]="FontWeight.BOLD"
        >
          Professional Certificate or Award
        </fl-text>
        <ng-container
          *ngIf="portfolioForm.get(CertificationFormInput.NAME) as control"
        >
          <fl-input
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfileCertificationEditAwardInput"
            [control]="control"
            placeholder="Enter professional certificate or award"
            i18n-placeholder="Professional certificate or award input"
          ></fl-input>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [colDesktopSmall]="6" [flMarginBottom]="Margin.MID">
        <fl-text
          i18n="Conferring organization label"
          [weight]="FontWeight.BOLD"
        >
          Conferring Organization
        </fl-text>
        <ng-container
          *ngIf="
            portfolioForm.get(CertificationFormInput.ORGANIZATION) as control
          "
        >
          <fl-input
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfileCertificationEditAwardOrganization"
            placeholder="Enter conferring organization"
            i18n-placeholder="university/college select input"
            [control]="control"
          ></fl-input>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-text i18n="Summary label" [weight]="FontWeight.BOLD">
          Summary
        </fl-text>
        <ng-container>
          <ng-container
            *ngIf="portfolioForm.get(CertificationFormInput.SUMMARY) as control"
          >
            <fl-textarea
              *ngIf="isFormControl(control)"
              flTrackingLabel="UserProfileCertificationEditAwardQualification"
              placeholder="Describe your qualification"
              i18n-placeholder="Summary input placeholder"
              [rows]="5"
              [control]="control"
            ></fl-textarea>
          </ng-container>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [colDesktopSmall]="3" [flMarginBottom]="Margin.MID">
        <fl-text
          i18n="Certification institution select label"
          [weight]="FontWeight.BOLD"
        >
          Start year
        </fl-text>
        <ng-container
          *ngIf="portfolioForm.get(CertificationFormInput.YEAR) as control"
        >
          <fl-select
            *ngIf="isFormControl(control)"
            flTrackingLabel="UserProfileCertificationEditAwardPlaceholder"
            placeholder="Select year"
            i18n-placeholder="Start year input placeholder"
            [control]="control"
            [options]="yearsList$ | flAsync"
          ></fl-select>
        </ng-container>
      </fl-col>

      <fl-col [col]="12" [flMarginBottom]="Margin.MID">
        <fl-banner-alert
          *ngIf="isError$ | flAsync"
          i18n="Error message label"
          [type]="BannerAlertType.ERROR"
        >
          Oops, something went wrong when saving your certification item. Please
          try again.
        </fl-banner-alert>
      </fl-col>

      <fl-col
        *ngIf="needsDeleteConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to delete this certification?"
          flTrackingLabel="UserProfileCertificationEditDelete"
          i18n-body="Delete certification confirmation body"
          i18n-title="Delete certification confirmation title"
          title="Delete Certification"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDeleteCertification()"
          (confirm)="handleConfirmDeleteCertification()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col
        *ngIf="needsCancelConfirmation$ | flAsync"
        [col]="12"
        [flMarginBottom]="Margin.XSMALL"
      >
        <app-user-profile-editable-ui-confirmation
          body="Are you sure you want to discard any changes?"
          flTrackingLabel="UserProfileCertificationEditDiscardChanges"
          i18n-body="Discard changes confirmation body"
          i18n-title="Discard changes confirmation title"
          title="Discard Changes"
          [isBusy]="isBusy$ | flAsync"
          (cancel)="handleCancelDiscardChanges()"
          (confirm)="handleConfirmDiscardChanges()"
        ></app-user-profile-editable-ui-confirmation>
      </fl-col>

      <fl-col *ngIf="!(hideActionRow$ | flAsync)" [col]="12">
        <app-user-profile-editable-ui-action-row
          deleteLinkText="Delete Certification"
          flTrackingLabel="UserProfileCertificationsEdit"
          i18n-deleteLinkText="Delete Certification link label"
          [disabled]="!portfolioForm.valid || !portfolioForm.dirty"
          [isBusy]="isBusy$ | flAsync"
          [showDeleteButton]="!!(currentCertificationId$ | flAsync)"
          (cancel)="handleCancel()"
          (delete)="handleDeleteCertification()"
          (save)="handleSave()"
        >
        </app-user-profile-editable-ui-action-row>
      </fl-col>
    </fl-grid>
  `,
})
export class UserProfileCertificationsEditComponent
  implements OnInit, OnDestroy {
  BannerAlertType = BannerAlertType;
  ButtonColor = ButtonColor;
  CertificationFormInput = CertificationFormInput;
  isFormControl = isFormControl;
  TextSize = TextSize;
  FontWeight = FontWeight;
  Margin = Margin;

  @Input() certification$: Rx.Observable<Certification | undefined>;
  @Input()
  certificationsCollection: DatastoreCollection<CertificationsCollection>;
  @Output() cancel = new EventEmitter<void>();

  private _isBusy$ = new Rx.BehaviorSubject<boolean>(false);
  isBusy$ = this._isBusy$.asObservable();

  private _isError$ = new Rx.BehaviorSubject<boolean>(false);
  isError$ = this._isError$.asObservable();

  private _needsDeleteConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsDeleteConfirmation$ = this._needsDeleteConfirmation$.asObservable();

  private _needsCancelConfirmation$ = new Rx.BehaviorSubject<boolean>(false);
  needsCancelConfirmation$ = this._needsCancelConfirmation$.asObservable();

  // If this is set the user is currently editing a certification and if it is
  // undefined the user is creating a new certification item.
  private _currentCertificationId$ = new Rx.BehaviorSubject<number | undefined>(
    undefined,
  );
  currentCertificationId$ = this._currentCertificationId$.asObservable();

  hideActionRow$: Rx.Observable<boolean>;
  yearsList$: Rx.Observable<readonly SelectAcceptedType[]>;

  certificationSubscription?: Rx.Subscription;
  nameControl = new FormControl('', [
    required($localize`Please enter your professional certificate or award.`),
    maxLength(100, $localize`Please enter at most 100 characters.`),
  ]);
  organizationControl = new FormControl('', [
    required($localize`Please enter a conferring organization.`),
    maxLength(100, $localize`Please enter at most 100 characters.`),
  ]);
  summaryControl = new FormControl('', [
    required($localize`Please describe your qualification or award.`),
    maxLength(500, $localize`Please enter at most 500 characters.`),
  ]);
  yearControl = new FormControl('', [
    required($localize`Please select a year.`),
  ]);

  portfolioForm = new FormGroup({
    [CertificationFormInput.NAME]: this.nameControl,
    [CertificationFormInput.ORGANIZATION]: this.organizationControl,
    [CertificationFormInput.SUMMARY]: this.summaryControl,
    [CertificationFormInput.YEAR]: this.yearControl,
  });

  constructor(private timeUtils: TimeUtils) {}

  ngOnInit(): void {
    this.hideActionRow$ = Rx.combineLatest([
      this._needsDeleteConfirmation$,
      this._needsCancelConfirmation$,
    ]).pipe(
      map(([deleteConfirm, cancelConfirm]) => deleteConfirm || cancelConfirm),
    );

    this.yearsList$ = this.timeUtils
      .getListOfYears(1900, new Date().getFullYear() + 1)
      .pipe(
        map(years =>
          years
            .map(year => ({
              displayText: year.toString(),
              value: year,
            }))
            .reverse(),
        ),
        // This is needed as to show the placeholder text.
        map(years => ['', ...years]),
      );

    this.certificationSubscription = this.certification$.subscribe(
      certification => {
        // If the certification is undefined we are are creating a new one.
        if (!certification) {
          this._currentCertificationId$.next(undefined);
          return;
        }

        this._currentCertificationId$.next(certification.id);
        this.nameControl.setValue(certification.certificate);
        this.organizationControl.setValue(certification.organization);
        this.summaryControl.setValue(certification.description);

        const awardedDateYear = new Date(
          certification.awardedDate,
        ).getFullYear();

        this.yearControl.setValue(awardedDateYear);
      },
    );
  }

  handleSave(): void {
    const certificationId = this._currentCertificationId$.getValue();
    // If we have a certification id the certification already exists so we need to
    // update it instead of create a new one.
    if (certificationId) {
      this.updateCertification(certificationId);
    } else {
      this.createNewCertification();
    }
  }

  createNewCertification(): void {
    this._isBusy$.next(true);
    this.certificationsCollection
      .push({
        userId: -1, // This is not used in the request.
        id: Date.now(),
        awardedDate: Date.UTC(this.yearControl.value, 1),
        certificate: this.nameControl.value,
        organization: this.organizationControl.value,
        description: this.summaryControl.value,
      })
      .then(response => {
        this._isBusy$.next(false);

        if (response.status === 'error') {
          this._isError$.next(true);
          return;
        }

        // Cancel out of the edit view on success.
        this.cancel.emit();
      });
  }

  updateCertification(certificationId: number): void {
    this._isBusy$.next(true);
    this.certificationsCollection
      .update(certificationId, {
        awardedDate: Date.UTC(this.yearControl.value, 1),
        certificate: this.nameControl.value,
        organization: this.organizationControl.value,
        description: this.summaryControl.value,
      })
      .then(response => {
        this._isBusy$.next(false);

        if (response.status === 'error') {
          this._isError$.next(true);
          return;
        }

        // Cancel out of the edit view on success.
        this.cancel.emit();
      });
  }

  handleConfirmDeleteCertification(): void {
    const certificationId = this._currentCertificationId$.getValue();
    // If this is not defined we should not be able to delete the certification.
    if (!certificationId) {
      return;
    }

    this._isBusy$.next(true);

    this.certificationsCollection.remove(certificationId).then(response => {
      this._needsDeleteConfirmation$.next(false);
      this._isBusy$.next(false);

      if (response.status === 'error') {
        this._isError$.next(true);
      }

      // Cancel out of the edit view on success.
      this.cancel.emit();
    });
  }

  handleCancelDeleteCertification(): void {
    this._needsDeleteConfirmation$.next(false);
  }

  handleCancelDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
  }

  handleConfirmDiscardChanges(): void {
    this._needsCancelConfirmation$.next(false);
    this.cancel.emit();
  }

  handleCancel(): void {
    if (this.portfolioForm.dirty) {
      this._needsCancelConfirmation$.next(true);
      return;
    }
    this.cancel.emit();
  }

  handleDeleteCertification(): void {
    this._needsDeleteConfirmation$.next(true);
  }

  ngOnDestroy(): void {
    if (this.certificationSubscription) {
      this.certificationSubscription.unsubscribe();
    }
  }
}
